import type { ReactNode } from 'react'

import { ResourceTile, ResourceTileGrid, StatusMessage } from '@krp/ui-kit'

import type {
  ResourcePickerActions,
  ResourcePickerButton,
  ResourcePickerCopy,
  ResourcePickerVm,
} from '../model/types'
import './resource-picker.css'

/**
 * «С чем работаем?»: выбор источника плитками (UI-RMK-FLOW 3.4). Источник — плитка, даже когда он один; у плитки одна
 * кнопка «Открыть». Экран ничего не знает ни о разделе, ни о виде источника: слова приходят в `copy`, плитки — в vm.
 * Источник не опрашивается — строка связи показывает то, что уже известно службе.
 */
export function ResourcePickerScreen({
  vm,
  actions,
  copy,
  renderButton,
}: {
  vm: ResourcePickerVm
  actions: ResourcePickerActions
  copy: ResourcePickerCopy
  /**
   * Кнопку рисует раздел: у каждой своя запись реестра действий (`actionId` — только строкой, её собирает тест реестра), чтобы
   * подсказка ⓘ называла раздел.
   */
  renderButton: (button: ResourcePickerButton) => ReactNode
}) {
  const button = (slot: ResourcePickerButton['slot'], label: string, variant: ResourcePickerButton['variant'], onClick: () => void) =>
    renderButton({ slot, label, variant, onClick })
  const state = vm.query.status
  if (state === 'idle' || state === 'loading') {
    return (
      <section className="resource-picker" aria-label={copy.sectionLabel}>
        <StatusMessage kind="loading" title={copy.loadingTitle} />
      </section>
    )
  }
  if (state === 'unauthorized') {
    return <StatusMessage kind="unauthorized" title="Сессия завершена" text="Войдите снова, чтобы продолжить." />
  }
  if (state === 'error' || state === 'unavailable') {
    return (
      <StatusMessage
        kind="unavailable"
        title={copy.unavailableTitle}
        {...(vm.query.error !== null ? { text: vm.query.error.message } : {})}
        action={button('reload', 'Повторить', 'secondary', actions.reload)}
      />
    )
  }
  if (vm.tiles.length === 0) {
    // 403 для кассира — то же, что «пусто»: доступа нет.
    return vm.canManageResources ? (
      <StatusMessage
        kind="empty"
        title={copy.emptyTitle}
        text={copy.emptyText}
        action={button('resources', 'Источники', 'secondary', actions.openResources)}
      />
    ) : (
      <StatusMessage kind="empty" title={copy.noAccessTitle} text={copy.noAccessText} />
    )
  }
  return (
    <section className="resource-picker" aria-labelledby="resource-picker-title">
      <header className="resource-picker__head">
        <h2 id="resource-picker-title">{copy.title}</h2>
      </header>
      <ResourceTileGrid>
        {vm.tiles.map((tile) => (
          <ResourceTile
            key={tile.id}
            mark={tile.mark}
            noun={tile.noun}
            name={tile.name}
            tone={tile.tone}
            down={tile.contactDown}
            lines={tile.lines}
            footer={button('open', copy.openLabel, 'primary', () => actions.open(tile.id))}
          />
        ))}
      </ResourceTileGrid>
      <div className="krp-list-reload">{button('reload', 'Обновить', 'ghost', actions.reload)}</div>
    </section>
  )
}
